import { useEffect, useRef, useState, RefObject } from "react";
import useGetUserPoint from "../../../hooks/useGetUserPoint";
import { usePostForm } from "../../../context/PostFormContext";
import AmountButtonStyles from "./AmountButtonStyles.module.css";
import { MentionInputHandle } from "../mentionInput/MentionInput";

const initialAmounts = [5, 10, 15, 20, 25];

type AmountButtonProps = {
  isOpen: boolean;
  toggleDropdown: () => void;
  mentionInputRef: RefObject<MentionInputHandle>;
};

const AmountButton = ({
  isOpen,
  toggleDropdown,
  mentionInputRef,
}: AmountButtonProps) => {
  const { formPost } = usePostForm();
  const { data } = useGetUserPoint();
  const [customAmount, setCustomAmount] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 6) return;
    setCustomAmount(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("Text");
    if (!/^\d+$/.test(pastedText)) {
      e.preventDefault();
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        toggleDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleDropdown]);

  // Reset customAmount when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setCustomAmount("");
    }
  }, [isOpen]);

  return (
    <div className={AmountButtonStyles.containerBlk} ref={dropdownRef}>
      <button
        className={AmountButtonStyles.buttonBlk}
        onClick={() => {
          if (!formPost.pointsGiven) toggleDropdown();
        }}
      >
        <i className="fa-solid fa-plus"></i> Amount
      </button>

      {isOpen && (
        <div className={AmountButtonStyles.amountDropDown}>
          <div className={AmountButtonStyles.flexBox}>
            {initialAmounts.map((amount, index) => (
              <button
                key={index}
                className={AmountButtonStyles.amountButton}
                onClick={() => {
                  mentionInputRef.current?.appendPointsChip(amount);
                  toggleDropdown();
                }}
              >
                +{amount}
              </button>
            ))}
          </div>

          <div className={AmountButtonStyles.flexBox}>
            <input
              type="number"
              placeholder="Enter specific amount"
              className={AmountButtonStyles.customInput}
              value={customAmount}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              maxLength={6}
              onChange={handleCustomAmountChange}
            />
            <button
              onClick={() => {
                const parsed = parseInt(customAmount, 10);
                if (!isNaN(parsed) && parsed > 0) {
                  mentionInputRef.current?.appendPointsChip(parsed);
                }
                toggleDropdown();
              }}
              className={AmountButtonStyles.submitBtn}
            >
              OK
            </button>
          </div>

          <p className={AmountButtonStyles.giveablePointsBlk}>
            <span>{data?.points?.giveablePoints}</span> Points available to give
          </p>
        </div>
      )}
    </div>
  );
};

export default AmountButton;
